import {AxiosResponse} from 'axios'
import {RawPagedCollectionResponse} from '../types'
import {HttpInstance} from '../createHttpInstance'

/**
 * Session attendees — the enrollment fact for a camper in a season.
 *
 * Path shape confirmed against the sessions service's own generated spec
 * (`sessions` repo, `gen/docs/external/openapi.yaml`, operation `ListAttendees`)
 * and its router registration under a `/v2` prefix. `clientid` and `seasonid`
 * are hard-required; the handler answers 400 without them.
 *
 * Note this is the COLLECTION form (`/v2/sessions/attendees`), not the
 * per-session `/v2/sessions/{id}/season/{seasonid}/attendees` operation this app
 * does not use — the two return different shapes.
 */
interface Params {
  ClientID: number
  SeasonID: number
  /** Repeated once per id. Omitted entirely when empty — meaning "every session". */
  SessionIDs?: number[]
  /**
   * Attendee status flag. Enrolled = 2, Applied = 4, WaitList = 8,
   * LeftEarly = 16, Cancelled = 32, Dismissed = 64, Inquiry = 128,
   * Withdrawn = 256, Incomplete = 512.
   */
  Status?: number
  PageNumber: number
  PageSize: number
}

export interface RawSessionProgramStatus {
  SessionID: number
  ProgramID: number
  StatusID: number
  StatusName: string
}

export interface RawSessionAttendee {
  ClientID: number
  PersonID: number
  SeasonID: number
  // Absent for an attendee row with no session/program membership rows.
  SessionProgramStatus: RawSessionProgramStatus[] | null
}

export type ListSessionAttendeesResponse =
  RawPagedCollectionResponse<RawSessionAttendee>

export interface ListSessionAttendees<P, R> {
  (http: HttpInstance, params: P): Promise<AxiosResponse<R>>
}

export const listSessionAttendees: ListSessionAttendees<
  Params,
  ListSessionAttendeesResponse
> = (http, params) => {
  const {ClientID, SeasonID, SessionIDs, Status, PageNumber, PageSize} = params

  const queryParams: string[] = []
  queryParams.push(`clientid=${ClientID}`)
  queryParams.push(`seasonid=${SeasonID}`)
  queryParams.push(`pagenumber=${PageNumber}`)
  queryParams.push(`pagesize=${PageSize}`)
  if (Status !== undefined) {
    queryParams.push(`status=${Status}`)
  }
  // An empty array must emit NO sessionids param at all: `sessionids=` would be
  // a filter for the empty session rather than "no session filter".
  ;(SessionIDs ?? []).forEach((id) => queryParams.push(`sessionids=${id}`))

  const query = `?${queryParams.join('&')}`

  return http.get<ListSessionAttendeesResponse>(
    `/v2/sessions/attendees${query}`,
  )
}
